import { useState } from 'react'
import { CookieConsent } from 'react-cookie-consent'
import { Link } from 'react-router'

/**
 * Hook para gestionar el consentimiento de cookies
 * @param initialConsent Estado inicial del consentimiento
 * @returns Objeto con el estado y funciones para gestionar el consentimiento
 */
export function useCookieConsent(initialConsent: boolean) {
  const [consentGiven, setConsentGiven] = useState<boolean>(initialConsent)

  // Manejadores para el consentimiento de cookies
  const handleAccept = () => {
    setConsentGiven(true)
    // Guardar en cookie
    document.cookie = `cookieConsent=true;path=/;max-age=${365 * 24 * 60 * 60};SameSite=Lax`
  }

  const handleDecline = () => {
    setConsentGiven(false)
    // Guardar en cookie
    document.cookie = `cookieConsent=false;path=/;max-age=${365 * 24 * 60 * 60};SameSite=Lax`
  }

  return {
    consentGiven,
    handleAccept,
    handleDecline
  }
}

/**
 * Componente de banner de consentimiento de cookies
 * @param props Propiedades del componente
 * @returns Componente React
 */
export function CookieBannerComponent({
  onAccept,
  onDecline,
  children
}: {
  onAccept: () => void
  onDecline: () => void
  children?: React.ReactNode
}) {
  return (
    <CookieConsent
      onAccept={onAccept}
      onDecline={onDecline}
      location="bottom"
      buttonText="Aceptar"
      declineButtonText="Rechazar"
      enableDeclineButton
      cookieName="cookieConsent" // Aunque gestionamos manualmente la cookie
      style={{ background: '#1c1c1c' }}
      buttonStyle={{ background: '#F59E0B', fontSize: '13px' }}
      declineButtonStyle={{ background: '#3f3f3f' }}
      expires={365}
      flipButtons
    >
      {children || (
        <>
          Este sitio web utiliza cookies para mejorar la experiencia del usuario.
          Consulta nuestra{' '}
          <Link to="/legal" className="text-bold text-amber-700">
            Política de privacidad
          </Link>{' '}
          para obtener más información.
        </>
      )}
    </CookieConsent>
  )
}

/**
 * Función para extraer el consentimiento de cookies de la solicitud HTTP
 * @param request Objeto Request de la solicitud HTTP
 * @returns Estado del consentimiento de cookies
 */
export function extractCookieConsent(request: Request): boolean {
  // Comprobar si hay consentimiento de cookies existente
  const cookieHeader = request.headers.get('Cookie')
  const cookieConsent = cookieHeader
    ? cookieHeader
        .split(';')
        .map((cookie: string) => cookie.trim().split('='))
        .find((parts: string[]) => parts[0] === 'cookieConsent')?.[1]
    : undefined

  // Parsear el valor de la cookie si existe
  let consentGiven = false

  try {
    if (cookieConsent) {
      consentGiven = cookieConsent === 'true'
    }
  } catch (e) {
    console.error('Error al parsear la cookie de consentimiento:', e)
  }

  return consentGiven
}
